import React from 'react'

import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import type { ListNode } from '@lexical/list'
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import type { HeadingTagType } from '@lexical/rich-text'
import { $createHeadingNode, $isHeadingNode } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import { $findMatchingParent } from '@lexical/utils'
import type { TextFormatType } from 'lexical'
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  COMMAND_PRIORITY_LOW,
  $createParagraphNode,
  $isElementNode,
  $isTextNode,
} from 'lexical'
import FormatAlignCenterIcon from 'mdi-react/FormatAlignCenterIcon'
import FormatAlignJustifyIcon from 'mdi-react/FormatAlignJustifyIcon'
import FormatAlignLeftIcon from 'mdi-react/FormatAlignLeftIcon'
import FormatAlignRightIcon from 'mdi-react/FormatAlignRightIcon'
import FormatBoldIcon from 'mdi-react/FormatBoldIcon'
import FormatColorHighlightIcon from 'mdi-react/FormatColorHighlightIcon'
import FormatColorTextIcon from 'mdi-react/FormatColorTextIcon'
import FormatItalicIcon from 'mdi-react/FormatItalicIcon'
import FormatLetterCaseIcon from 'mdi-react/FormatLetterCaseIcon'
import FormatListBulletedIcon from 'mdi-react/FormatListBulletedIcon'
import FormatListNumberedIcon from 'mdi-react/FormatListNumberedIcon'
import FormatStrikethroughIcon from 'mdi-react/FormatStrikethroughIcon'
import FormatSubscriptIcon from 'mdi-react/FormatSubscriptIcon'
import FormatSuperscriptIcon from 'mdi-react/FormatSuperscriptIcon'
import FormatUnderlineIcon from 'mdi-react/FormatUnderlineIcon'
import LinkIcon from 'mdi-react/LinkIcon'

import { ColorPicker } from './ColorPicker'
import { ToolbarButton } from './ToolbarButton'

import { Button } from '../../Button'

type ToolbarPluginProps = {
  config: {
    enableHeadings?: boolean
    enableFontSize?: boolean
    enableBold?: boolean
    enableItalic?: boolean
    enableUnderline?: boolean
    enableLists?: boolean
    enableLinks?: boolean
    enableAlignment?: boolean
    enableFontColor?: boolean
    enableHighlight?: boolean
    enableTextTransform?: boolean
  }
}

export const ToolbarPlugin: React.FC<ToolbarPluginProps> = ({ config }) => {
  const [editor] = useLexicalComposerContext()
  const [selectedHeading, setSelectedHeading] = React.useState<string>('normal')
  const [fontSize, setFontSize] = React.useState<string>('16px')
  const [isBold, setIsBold] = React.useState(false)
  const [isItalic, setIsItalic] = React.useState(false)
  const [isUnderline, setIsUnderline] = React.useState(false)
  const [isStrikethrough, setIsStrikethrough] = React.useState(false)
  const [isSubscript, setIsSubscript] = React.useState(false)
  const [isSuperscript, setIsSuperscript] = React.useState(false)
  const [isBulletList, setIsBulletList] = React.useState(false)
  const [isNumberedList, setIsNumberedList] = React.useState(false)
  const [isLink, setIsLink] = React.useState(false)
  const [showLinkDialog, setShowLinkDialog] = React.useState(false)
  const [linkUrl, setLinkUrl] = React.useState('')
  const [alignment, setAlignment] = React.useState<string | number>('left')
  const [fontColor, setFontColor] = React.useState<string>('#000000')
  const [, setHighlightColor] = React.useState<string>('#ffff00')
  const [showColorPicker, setShowColorPicker] = React.useState(false)
  const [showHighlightPicker, setShowHighlightPicker] = React.useState(false)
  const [showTextTransformMenu, setShowTextTransformMenu] = React.useState(false)

  const updateToolbar = React.useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      // Update text format
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))
      setIsSubscript(selection.hasFormat('subscript'))
      setIsSuperscript(selection.hasFormat('superscript'))

      // Update heading
      const anchorNode = selection.anchor.getNode()
      const element =
        anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow()

      if ($isHeadingNode(element)) {
        const tag = element.getTag()
        setSelectedHeading(tag)
      } else {
        setSelectedHeading('normal')
      }

      // Update alignment
      const elementFormat = element.getFormat()
      setAlignment(elementFormat || 'left')

      // Update list state
      const listNode = $findMatchingParent(anchorNode, node => $isListNode(node)) as ListNode | null

      if (listNode) {
        const listType = listNode.getListType()
        setIsBulletList(listType === 'bullet')
        setIsNumberedList(listType === 'number')
      } else {
        setIsBulletList(false)
        setIsNumberedList(false)
      }

      // Update link state
      const linkNode = $findMatchingParent(anchorNode, $isLinkNode)
      setIsLink(linkNode !== null)
    }
  }, [])

  React.useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar()
        })
      }),
    [editor, updateToolbar]
  )

  React.useEffect(
    () =>
      editor.registerCommand(
        FORMAT_TEXT_COMMAND,
        () => {
          updateToolbar()
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
    [editor, updateToolbar]
  )

  const formatHeading = (headingTag: HeadingTagType | 'normal') => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        if (headingTag === 'normal') {
          $setBlocksType(selection, () => $createParagraphNode())
        } else {
          $setBlocksType(selection, () => $createHeadingNode(headingTag))
        }
      }
    })
  }

  const formatText = (format: TextFormatType) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
  }

  const formatAlignment = (align: 'left' | 'center' | 'right' | 'justify') => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, align)
  }

  const changeFontSize = (size: string) => {
    setFontSize(size)
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        // Apply font size using style
        const nodes = selection.getNodes()
        nodes.forEach(node => {
          if (node.getType() === 'text' && $isElementNode(node)) {
            node.setStyle(`font-size: ${size}`)
          }
        })
      }
    })
  }

  const changeTextColor = (color: string) => {
    setFontColor(color)
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes()
        nodes.forEach(node => {
          if (node.getType() === 'text' && $isElementNode(node)) {
            const style = node.getStyle()
            node.setStyle(`${style}; color: ${color}`)
          }
        })
      }
    })
  }

  const changeHighlightColor = (color: string) => {
    setHighlightColor(color)
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes()
        nodes.forEach(node => {
          if (node.getType() === 'text' && $isElementNode(node)) {
            const style = node.getStyle()
            node.setStyle(`${style}; background-color: ${color}`)
          }
        })
      }
    })
  }

  const transformText = (transform: 'uppercase' | 'lowercase' | 'capitalize') => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes()
        nodes.forEach(node => {
          if (node.getType() === 'text' && $isTextNode(node)) {
            const text = node.getTextContent()
            let transformedText = text

            switch (transform) {
              case 'uppercase':
                transformedText = text.toUpperCase()
                break
              case 'lowercase':
                transformedText = text.toLowerCase()
                break
              case 'capitalize':
                transformedText = text.replace(/\b\w/g, l => l.toUpperCase())
                break
            }

            node.setTextContent(transformedText)
          }
        })
      }
    })
    setShowTextTransformMenu(false)
  }

  const toggleList = (listType: 'bullet' | 'number') => {
    if (listType === 'bullet') {
      editor.dispatchCommand(
        isBulletList ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
        undefined
      )
    } else {
      editor.dispatchCommand(
        isNumberedList ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
        undefined
      )
    }
  }

  const insertLink = () => {
    if (!isLink) {
      setShowLinkDialog(true)
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    }
  }

  const confirmLink = () => {
    if (linkUrl) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
        url: linkUrl,
        target: '_blank',
        rel: 'noopener noreferrer',
      })
      setShowLinkDialog(false)
      setLinkUrl('')
    }
  }

  return (
    <div className="toolbar flex flex-wrap items-center gap-1 rounded-t-lg border border-b-0 border-solid border-gray-300 p-2">
      {config.enableHeadings && (
        <>
          <select
            value={selectedHeading}
            onChange={e => formatHeading(e.target.value as HeadingTagType | 'normal')}
            className="rounded border border-gray-300 px-3 py-1 text-sm"
          >
            <option value="normal">Normal</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
          </select>
          <div className="mx-1 h-6 w-px bg-gray-300" />
        </>
      )}

      {config.enableFontSize && (
        <>
          <select
            value={fontSize}
            onChange={e => changeFontSize(e.target.value)}
            className="rounded border border-gray-300 px-3 py-1 text-sm"
          >
            <option value="12px">12px</option>
            <option value="14px">14px</option>
            <option value="16px">16px</option>
            <option value="18px">18px</option>
            <option value="20px">20px</option>
            <option value="24px">24px</option>
            <option value="28px">28px</option>
            <option value="32px">32px</option>
          </select>
          <div className="mx-1 h-6 w-px bg-gray-300" />
        </>
      )}

      {config.enableBold && (
        <ToolbarButton onClick={() => formatText('bold')} isActive={isBold} aria-label="Bold">
          <FormatBoldIcon size={18} />
        </ToolbarButton>
      )}

      {config.enableItalic && (
        <ToolbarButton onClick={() => formatText('italic')} isActive={isItalic} aria-label="Italic">
          <FormatItalicIcon size={18} />
        </ToolbarButton>
      )}

      {config.enableUnderline && (
        <ToolbarButton
          onClick={() => formatText('underline')}
          isActive={isUnderline}
          aria-label="Underline"
        >
          <FormatUnderlineIcon size={18} />
        </ToolbarButton>
      )}

      <ToolbarButton
        onClick={() => formatText('strikethrough')}
        isActive={isStrikethrough}
        aria-label="Strikethrough"
      >
        <FormatStrikethroughIcon size={18} />
      </ToolbarButton>

      {config.enableTextTransform && (
        <>
          <div className="mx-1 h-6 w-px bg-gray-300" />
          <ToolbarButton
            onClick={() => formatText('subscript')}
            isActive={isSubscript}
            aria-label="Subscript"
          >
            <FormatSubscriptIcon size={18} />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => formatText('superscript')}
            isActive={isSuperscript}
            aria-label="Superscript"
          >
            <FormatSuperscriptIcon size={18} />
          </ToolbarButton>

          <div className="relative">
            <ToolbarButton
              onClick={() => setShowTextTransformMenu(!showTextTransformMenu)}
              aria-label="Text Transform"
            >
              <FormatLetterCaseIcon size={18} />
            </ToolbarButton>

            {showTextTransformMenu && (
              <div className="absolute left-0 top-10 z-10 rounded border border-gray-300 bg-white p-1 shadow-lg">
                <button
                  onClick={() => transformText('uppercase')}
                  className="block w-full px-3 py-1 text-left text-sm hover:bg-gray-100"
                >
                  UPPERCASE
                </button>
                <button
                  onClick={() => transformText('lowercase')}
                  className="block w-full px-3 py-1 text-left text-sm hover:bg-gray-100"
                >
                  lowercase
                </button>
                <button
                  onClick={() => transformText('capitalize')}
                  className="block w-full px-3 py-1 text-left text-sm hover:bg-gray-100"
                >
                  Capitalize
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {config.enableFontColor && (
        <>
          <div className="mx-1 h-6 w-px bg-gray-300" />
          <div className="relative">
            <ToolbarButton
              onClick={() => setShowColorPicker(!showColorPicker)}
              aria-label="Text Color"
            >
              <FormatColorTextIcon size={18} style={{ color: fontColor }} />
            </ToolbarButton>

            {showColorPicker && (
              <ColorPicker
                onClick={color => {
                  changeTextColor(color)
                  setShowColorPicker(false)
                }}
              />
            )}
          </div>
        </>
      )}

      {config.enableHighlight && (
        <div className="relative">
          <ToolbarButton
            onClick={() => setShowHighlightPicker(!showHighlightPicker)}
            isActive={showHighlightPicker}
            aria-label="Highlight"
          >
            <FormatColorHighlightIcon size={18} />
          </ToolbarButton>

          {showHighlightPicker && (
            <ColorPicker
              onClick={color => {
                changeHighlightColor(color)
                setShowHighlightPicker(false)
              }}
            />
          )}
        </div>
      )}

      {config.enableAlignment && (
        <>
          <div className="mx-1 h-6 w-px bg-gray-300" />
          <ToolbarButton
            onClick={() => formatAlignment('left')}
            isActive={alignment === 'left'}
            aria-label="Align Left"
          >
            <FormatAlignLeftIcon size={18} />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => formatAlignment('center')}
            isActive={alignment === 'center'}
            aria-label="Align Center"
          >
            <FormatAlignCenterIcon size={18} />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => formatAlignment('right')}
            isActive={alignment === 'right'}
            aria-label="Align Right"
          >
            <FormatAlignRightIcon size={18} />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => formatAlignment('justify')}
            isActive={alignment === 'justify'}
            aria-label="Align Justify"
          >
            <FormatAlignJustifyIcon size={18} />
          </ToolbarButton>
        </>
      )}

      {config.enableLists && (
        <>
          <div className="mx-1 h-6 w-px bg-gray-300" />
          <ToolbarButton
            onClick={() => toggleList('bullet')}
            isActive={isBulletList}
            aria-label="Bullet List"
          >
            <FormatListBulletedIcon size={18} />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => toggleList('number')}
            isActive={isNumberedList}
            aria-label="Numbered List"
          >
            <FormatListNumberedIcon size={18} />
          </ToolbarButton>
        </>
      )}

      {config.enableLinks && (
        <>
          <div className="mx-1 h-6 w-px bg-gray-300" />
          <ToolbarButton onClick={insertLink} isActive={isLink} aria-label="Insert Link">
            <LinkIcon size={18} />
          </ToolbarButton>
        </>
      )}

      {showLinkDialog && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50">
          <div className="absolute left-1/2 top-[350px] w-[450px] -translate-x-1/2 transform rounded-lg bg-white p-4 shadow-lg">
            <h3 className="mb-2 text-lg font-semibold">Insert Link</h3>
            <input
              type="text"
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className="mb-3 w-full rounded border border-gray-300 px-3 py-2"
            />
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => {
                  setShowLinkDialog(false)
                  setLinkUrl('')
                }}
              >
                Cancel
              </Button>
              <Button onClick={confirmLink} variant="primary">
                Insert
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
